import { useState, useEffect } from 'react';
import { salesAPI, alertsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
    AlertCircle, 
    CheckCircle2, 
    Loader2,
    Banknote,
    CreditCard,
    Building2,
    Clock,
    Mail
} from 'lucide-react';

const Dues = () => {
    const { isAdmin } = useAuth();
    const [dues, setDues] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDue, setSelectedDue] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState('cash');
    const [processing, setProcessing] = useState(false);

    useEffect(() => {
        fetchDues();
    }, []);

    const fetchDues = async () => {
        try {
            const response = await salesAPI.getDues();
            setDues(response.data);
        } catch (error) {
            console.error('Failed to fetch dues:', error);
            toast.error('Failed to load due payments');
        } finally {
            setLoading(false);
        }
    };

    const handleMarkAsPaid = async () => {
        if (!selectedDue) return;

        setProcessing(true);
        try {
            // For SPLIT sales we must NOT override `payment_method` — the
            // sale may still have pending cheques and other lines that
            // need it to stay "split" so /cheques etc. keep showing it.
            // The backend now also enforces this, but we drop the field
            // client-side too for clarity.
            const payload = { is_due_paid: true };
            if (selectedDue.payment_method !== 'split') {
                payload.payment_method = paymentMethod;
            }
            await salesAPI.update(selectedDue.id, payload);
            toast.success('Payment marked as received!');
            setSelectedDue(null);
            fetchDues();
        } catch (error) {
            toast.error('Failed to update payment');
        } finally {
            setProcessing(false);
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR',
        }).format(value || 0);
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    };

    const getPendingDays = (createdAt) => {
        const created = new Date(createdAt);
        const today = new Date();
        const diffTime = Math.abs(today - created);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    const getPendingDaysBadge = (days) => {
        if (days <= 7) {
            return <Badge className="bg-green-100 text-green-700">{days} days</Badge>;
        } else if (days <= 30) {
            return <Badge className="bg-yellow-100 text-yellow-700">{days} days</Badge>;
        } else {
            return <Badge className="bg-red-100 text-red-700">{days} days</Badge>;
        }
    };

    // `due_amount` is the canonical outstanding figure surfaced by the
    // backend (full invoice for single-method-due sales; sum of `due`
    // lines for split sales). Fall back to `amount` for legacy rows.
    const dueOf = (d) => (d.due_amount != null ? d.due_amount : d.amount) || 0;
    const totalDue = dues.reduce((sum, d) => sum + dueOf(d), 0);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <Loader2 className="w-8 h-8 animate-spin text-[#C08478]" />
            </div>
        );
    }

    return (
        <div data-testid="dues-page" className="space-y-8 animate-fadeIn">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-[#1A1A1A] tracking-tight">Due Payments</h1>
                    <p className="text-gray-500 mt-1">Manage pending payments from customers</p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    {isAdmin && dues.length > 0 && (
                        <Button
                            onClick={async () => {
                                try {
                                    const { data } = await alertsAPI.sendDueReminder();
                                    toast.success(`Email sent (${data.dues_count} invoices)`);
                                } catch (err) {
                                    toast.error(err?.response?.data?.detail || 'Email failed');
                                }
                            }}
                            data-testid="send-dues-reminder"
                            variant="outline"
                            className="border-[#E76F51] text-[#E76F51] hover:bg-[#E76F51]/10"
                        >
                            <Mail size={16} className="mr-2" /> Email Reminder
                        </Button>
                    )}
                    <Card className="border-0 shadow-sm bg-[#E76F51]/10">
                        <CardContent className="p-4 flex items-center gap-3">
                            <AlertCircle className="text-[#E76F51]" size={24} />
                            <div>
                                <p className="text-sm text-gray-600">Total Outstanding</p>
                                <p className="text-2xl font-bold text-[#E76F51] euro-amount">
                                    {formatCurrency(totalDue)}
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Dues Table */}
            <Card className="border-0 shadow-sm">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <AlertCircle className="text-[#E76F51]" size={20} />
                        Pending Payments ({dues.length})
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {dues.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                            <CheckCircle2 size={48} className="mb-3 text-[#2A9D8F]" />
                            <p className="text-lg font-medium text-[#1A1A1A]">All caught up!</p>
                            <p className="text-sm">No pending due payments</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-[#F5F5F5]">
                                        <TableHead className="font-medium">Invoice</TableHead>
                                        <TableHead className="font-medium">Company</TableHead>
                                        <TableHead className="font-medium">Amount</TableHead>
                                        <TableHead className="font-medium">Date</TableHead>
                                        <TableHead className="font-medium">Pending Days</TableHead>
                                        <TableHead className="font-medium">Recorded By</TableHead>
                                        <TableHead className="text-right font-medium">Action</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {dues.map((due) => {
                                        const pendingDays = getPendingDays(due.created_at);
                                        return (
                                            <TableRow key={due.id} className="hover:bg-[#FAFAFA]">
                                                <TableCell className="font-mono text-sm font-medium">
                                                    {due.invoice_number}
                                                </TableCell>
                                                <TableCell className="text-gray-700">
                                                    {due.company_name || '-'}
                                                </TableCell>
                                                <TableCell>
                                                    <span className="text-lg font-bold text-[#E76F51] euro-amount">
                                                        {formatCurrency(dueOf(due))}
                                                    </span>
                                                    {due.payment_method === 'split' && due.amount > dueOf(due) && (
                                                        <div className="text-[10px] text-gray-400 mt-0.5">
                                                            of {formatCurrency(due.amount)} invoice
                                                        </div>
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-gray-600">
                                                    {formatDate(due.created_at)}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        <Clock size={14} className="text-gray-400" />
                                                        {getPendingDaysBadge(pendingDays)}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-gray-600">
                                                    {due.created_by_name}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <Button
                                                        data-testid={`mark-paid-${due.id}`}
                                                        onClick={() => setSelectedDue(due)}
                                                        className="bg-[#2A9D8F] hover:bg-[#238b7e] text-white rounded-full px-4"
                                                    >
                                                        <CheckCircle2 size={16} className="mr-2" />
                                                        Mark Paid
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Mark as Paid Dialog */}
            <Dialog open={!!selectedDue} onOpenChange={() => setSelectedDue(null)}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Mark Payment as Received</DialogTitle>
                    </DialogHeader>
                    
                    {selectedDue && (
                        <div className="space-y-6 py-4">
                            <div className="bg-[#F5F5F5] rounded-xl p-4">
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-gray-500">Invoice</span>
                                    <span className="font-mono font-medium">{selectedDue.invoice_number}</span>
                                </div>
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-gray-500">Amount</span>
                                    <span className="text-xl font-bold text-[#1A1A1A] euro-amount">
                                        {formatCurrency(dueOf(selectedDue))}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-500">Pending Days</span>
                                    <span className="font-medium text-[#E76F51]">
                                        {getPendingDays(selectedDue.created_at)} days
                                    </span>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label>Payment Method Received</Label>
                                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                                    <SelectTrigger data-testid="payment-method-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="cash">
                                            <div className="flex items-center gap-2">
                                                <Banknote size={16} className="text-[#2A9D8F]" />
                                                Cash
                                            </div>
                                        </SelectItem>
                                        <SelectItem value="card">
                                            <div className="flex items-center gap-2">
                                                <CreditCard size={16} className="text-[#264653]" />
                                                Card
                                            </div>
                                        </SelectItem>
                                        <SelectItem value="bank_transfer">
                                            <div className="flex items-center gap-2">
                                                <Building2 size={16} className="text-[#E9C46A]" />
                                                Bank Transfer
                                            </div>
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    )}

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setSelectedDue(null)}
                            className="rounded-full"
                        >
                            Cancel
                        </Button>
                        <Button
                            data-testid="confirm-paid-btn"
                            onClick={handleMarkAsPaid}
                            disabled={processing}
                            className="bg-[#2A9D8F] hover:bg-[#238b7e] text-white rounded-full"
                        >
                            {processing ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                    Processing...
                                </>
                            ) : (
                                'Confirm Payment'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Dues;
